import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactElement,
} from "react";
import { Search } from "lucide-react";
import { StatusDot } from "../atoms/StatusDot/index.js";
import {
  Popover,
  PopoverContent,
  PopoverMenuItem,
  PopoverTrigger,
} from "../molecules/Popover/Popover.js";

export interface WorkspaceWidgetPickerItem {
  readonly id: string;
  readonly label: string;
  readonly description?: string;
  readonly group?: string;
  readonly keywords?: readonly string[];
  /** Visible explanation; an unavailable entry stays discoverable but inert. */
  readonly disabledReason?: string;
}

export interface WorkspaceWidgetPickerProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly trigger: ReactElement;
  readonly items: readonly WorkspaceWidgetPickerItem[];
  readonly onSelect: (id: string) => void;
  readonly label?: string;
  readonly searchPlaceholder?: string;
  readonly emptyMessage?: string;
  readonly align?: "start" | "end" | "center";
}

/** Data-blind searchable picker shared by canvas modules. */
export function WorkspaceWidgetPicker({
  open,
  onOpenChange,
  trigger,
  items,
  onSelect,
  label = "Add widget",
  searchPlaceholder = "Search widgets",
  emptyMessage = "No widgets match your search.",
  align = "end",
}: WorkspaceWidgetPickerProps): ReactElement {
  return (
    <Popover
      open={open}
      onOpenChange={onOpenChange}
      align={align}
      contentRole="dialog"
    >
      <PopoverTrigger>{trigger}</PopoverTrigger>
      <PopoverContent
        aria-label={label}
        padding="sm"
        className="w-80"
        maxHeight={440}
      >
        <WorkspaceWidgetPickerPanel
          items={items}
          onSelect={onSelect}
          label={label}
          searchPlaceholder={searchPlaceholder}
          emptyMessage={emptyMessage}
        />
      </PopoverContent>
    </Popover>
  );
}

interface WorkspaceWidgetPickerPanelProps {
  readonly items: readonly WorkspaceWidgetPickerItem[];
  readonly onSelect: (id: string) => void;
  readonly label: string;
  readonly searchPlaceholder: string;
  readonly emptyMessage: string;
}

/** Mounted only while the popover is present, so query state resets on close. */
function WorkspaceWidgetPickerPanel({
  items,
  onSelect,
  label,
  searchPlaceholder,
  emptyMessage,
}: WorkspaceWidgetPickerPanelProps): ReactElement {
  const scope = useId();
  const listId = `${scope}-list`;
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState("");
  const [activeId, setActiveId] = useState<string | null>(null);

  const filtered = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase();
    if (!needle) return items;
    return items.filter((item) =>
      [item.label, item.description, item.group, ...(item.keywords ?? [])]
        .filter((value): value is string => Boolean(value))
        .some((value) => value.toLocaleLowerCase().includes(needle)),
    );
  }, [items, query]);
  const enabled = useMemo(
    () => filtered.filter((item) => !item.disabledReason),
    [filtered],
  );
  const resolvedActiveId =
    activeId && enabled.some((item) => item.id === activeId)
      ? activeId
      : (enabled[0]?.id ?? null);
  const optionId = (itemId: string): string =>
    `${scope}-option-${encodeURIComponent(itemId)}`;

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => inputRef.current?.focus());
    return () => window.cancelAnimationFrame(frame);
  }, []);

  const grouped = useMemo(() => {
    const groups = new Map<string, WorkspaceWidgetPickerItem[]>();
    for (const item of filtered) {
      const group = item.group ?? "";
      groups.set(group, [...(groups.get(group) ?? []), item]);
    }
    return [...groups.entries()];
  }, [filtered]);

  const moveActive = (delta: -1 | 1): void => {
    if (enabled.length === 0) return;
    const current = enabled.findIndex((item) => item.id === resolvedActiveId);
    const next =
      current < 0
        ? delta > 0
          ? 0
          : enabled.length - 1
        : (current + delta + enabled.length) % enabled.length;
    const nextId = enabled[next]?.id ?? null;
    setActiveId(nextId);
    if (nextId)
      document
        .getElementById(optionId(nextId))
        ?.scrollIntoView({ block: "nearest" });
  };

  const onSearchKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      moveActive(event.key === "ArrowDown" ? 1 : -1);
      return;
    }
    if (event.key === "Home" || event.key === "End") {
      event.preventDefault();
      setActiveId(
        event.key === "Home"
          ? (enabled[0]?.id ?? null)
          : (enabled.at(-1)?.id ?? null),
      );
      return;
    }
    if (event.key === "Enter" && resolvedActiveId) {
      event.preventDefault();
      (
        document.getElementById(
          optionId(resolvedActiveId),
        ) as HTMLButtonElement | null
      )?.click();
    }
  };

  return (
    <>
      <div className="flex h-9 items-center gap-2 rounded-lg border border-pb-control-border bg-pb-card px-2.5 focus-frame">
        <Search className="h-4 w-4 shrink-0 text-pb-text-muted" aria-hidden />
        <input
          ref={inputRef}
          role="combobox"
          aria-label={searchPlaceholder}
          aria-autocomplete="list"
          aria-controls={listId}
          aria-expanded="true"
          aria-activedescendant={
            resolvedActiveId ? optionId(resolvedActiveId) : undefined
          }
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={onSearchKeyDown}
          placeholder={searchPlaceholder}
          className="min-w-0 flex-1 bg-transparent text-sm text-pb-text placeholder:text-pb-text-muted focus:outline-none"
        />
      </div>

      <div
        id={listId}
        role="listbox"
        aria-label="Available widgets"
        className="mt-2"
      >
        {grouped.length === 0 ? (
          <p className="px-2 py-6 text-center text-sm text-pb-text-muted">
            {emptyMessage}
          </p>
        ) : (
          grouped.map(([group, groupItems]) => (
            <div
              key={group || "ungrouped"}
              role="group"
              aria-label={group || label}
            >
              {group ? (
                <div className="px-2 pb-1 pt-2 text-dense-1 font-semibold uppercase tracking-wider text-pb-text-muted">
                  {group}
                </div>
              ) : null}
              {groupItems.map((item) => {
                const disabled = Boolean(item.disabledReason);
                return (
                  <PopoverMenuItem
                    key={item.id}
                    id={optionId(item.id)}
                    role="option"
                    aria-selected={item.id === resolvedActiveId}
                    aria-disabled={disabled || undefined}
                    disabled={disabled}
                    /* The info dot (OK-94): teal marks a selection only. */
                    leading={<StatusDot tone="info" size="sm" />}
                    {...((item.disabledReason ?? item.description)
                      ? {
                          description: item.disabledReason ?? item.description,
                        }
                      : {})}
                    /* The roving highlight is the neutral overlay
                       (AP-17), not a teal selection. */
                    active={item.id === resolvedActiveId && !disabled}
                    onMouseMove={() => {
                      if (!disabled) setActiveId(item.id);
                    }}
                    onClick={() => onSelect(item.id)}
                  >
                    {item.label}
                  </PopoverMenuItem>
                );
              })}
            </div>
          ))
        )}
      </div>
    </>
  );
}
